import {noop} from '@benchling/js-utils';
import {Button} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import StepConfigEditorRouterV2 from 'pipelines/generalized-step-config-editor/editors/StepConfigEditorRouterV2';
import {StepConfigEditorContext} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon, {isValidBenchlingIcon} from 'ui-library/icons/BenchlingIcon';
import {JSONValue} from 'util/Types';

import {DATA_LAYOUT_INPUT_DATA_NAME} from './DataLayoutConstants';
import {useDataLayoutContext} from './DataLayoutContext';
import {
  ConfigFormBlock as ConfigFormBlockType,
  ConfigFormFieldEvent,
  ConfigFormField as ConfigFormFieldType,
} from './DataLayoutTypes';

interface ConfigFormFieldProps {
  configFormField: ConfigFormFieldType;
  draftConfigOptions?: {
    draftConfig?: JSONValue;
    setDraftConfig: (newValue: JSONValue) => void;
  };
  layoutPath: Array<string | number>;
}

export function ConfigFormField({
  configFormField: {configDataFrameSpec, configDataFrameId, ...stepConfigEditorRouterV2Props},
  draftConfigOptions,
  layoutPath,
}: ConfigFormFieldProps): JSX.Element {
  const {clientId, onEvents} = useDataLayoutContext();
  const contextValue = useMemo(
    () => ({
      dataFrameId: configDataFrameId,
      inputDataFrames: {[DATA_LAYOUT_INPUT_DATA_NAME]: [configDataFrameSpec]},
      clientStateId: clientId,
    }),
    [configDataFrameId, configDataFrameSpec, clientId]
  );

  return (
    <StepConfigEditorContext.Provider value={contextValue}>
      <StepConfigEditorRouterV2
        {...stepConfigEditorRouterV2Props}
        config={
          draftConfigOptions != null && draftConfigOptions.draftConfig != null
            ? {
                field: draftConfigOptions.draftConfig,
              }
            : stepConfigEditorRouterV2Props.config
        }
        disabled={onEvents == null}
        onChangeConfig={
          onEvents == null
            ? noop
            : (config: JSONValue, path: Array<string>, newValue: JSONValue) => {
                if (draftConfigOptions != null) {
                  draftConfigOptions.setDraftConfig(newValue);
                } else {
                  onEvents([{type: 'CONFIG_FORM_FIELD', layoutPath, newValue}]);
                }
              }
        }
      />
    </StepConfigEditorContext.Provider>
  );
}

const draftConfigFormGlobalState = makeUseGlobalClientState<Record<string, JSONValue | undefined>>({});

interface ConfigFormBlockProps {
  block: ConfigFormBlockType;
  layoutPath: Array<string | number>;
}

export default function ConfigFormBlock({
  block: {configFields, submitOptions},
  layoutPath,
}: ConfigFormBlockProps): JSX.Element {
  const {clientId, onEvents} = useDataLayoutContext();

  const blockClientStateId = useMemo(() => `${clientId}-${layoutPath.join('-')}`, [clientId, layoutPath]);

  const [draftConfigFormState, setDraftConfigFormState] =
    draftConfigFormGlobalState.useGlobalClientState(blockClientStateId);

  function handleOnSubmit(): void {
    if (Object.keys(draftConfigFormState).length === 0) {
      return;
    }

    assert(onEvents != null, 'Expected onEvents to be defined when calling handleOnSubmit');

    onEvents(
      configFields
        .map((_configFormField, index) =>
          draftConfigFormState[`field-${index}`] != null
            ? {
                type: 'CONFIG_FORM_FIELD',
                layoutPath: [...layoutPath, 'configFields', index],
                newValue: draftConfigFormState[`field-${index}`],
              }
            : undefined
        )
        .filter((maybeEvent): maybeEvent is ConfigFormFieldEvent => maybeEvent != null)
    );

    setDraftConfigFormState({});
  }

  const submitButtonIcon = submitOptions?.submitButtonIcon;

  assert(
    submitButtonIcon == null || isValidBenchlingIcon(submitButtonIcon),
    'submitButtonIcon is not a valid Benchling icon'
  );

  return (
    <div className={css(styles.configFormBlockContainer)}>
      {configFields.map((configFormField, index) => (
        <ConfigFormField
          key={index}
          configFormField={configFormField}
          layoutPath={[...layoutPath, 'configFields', index]}
          draftConfigOptions={
            submitOptions != null
              ? {
                  draftConfig: draftConfigFormState[`field-${index}`],
                  setDraftConfig: (newValue: JSONValue) => {
                    setDraftConfigFormState({
                      ...draftConfigFormState,
                      [`field-${index}`]: newValue,
                    });
                  },
                }
              : undefined
          }
        />
      ))}
      {submitOptions && onEvents != null && submitButtonIcon != null && (
        <Button
          leftIcon={<BenchlingIcon icon={submitButtonIcon} />}
          onClick={handleOnSubmit}
          variant="primary"
        >
          {submitOptions.submitButtonText}
        </Button>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  configFormBlockContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
});
